import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  upsertPaymentToCheck,
  type PaymentToCheck,
} from '../../utils/mpesaPaymentsToCheck';

/**
 * Owner-side "Payments to check" (M-Pesa prompts needing a person), keyed by
 * location. Runtime only: the M-Pesa Integration header, the list and the
 * details screen all read the same rows, so a payment settled on one screen
 * is gone from the others without a refetch.
 */

export interface MpesaPaymentsToCheckLocationEntry {
  items: PaymentToCheck[];
  loading: boolean;
  /** The last load failed; `items` keeps whatever loaded before. */
  error: boolean;
  loadedAt: string | null;
  /** Prompt id with an action in flight. */
  busyId: string | null;
}

export interface MpesaPaymentsToCheckState {
  byLocation: Record<string, MpesaPaymentsToCheckLocationEntry>;
}

const initialState: MpesaPaymentsToCheckState = { byLocation: {} };

const emptyEntry = (): MpesaPaymentsToCheckLocationEntry => ({
  items: [],
  loading: false,
  error: false,
  loadedAt: null,
  busyId: null,
});

const entryFor = (state: MpesaPaymentsToCheckState, locationId: string) => {
  if (!state.byLocation[locationId]) state.byLocation[locationId] = emptyEntry();
  return state.byLocation[locationId];
};

const mpesaPaymentsToCheckSlice = createSlice({
  name: 'backofficeMpesaPaymentsToCheck',
  initialState,
  reducers: {
    setPaymentsToCheckLoading: (
      state,
      action: PayloadAction<{ locationId: string; loading: boolean }>,
    ) => {
      const { locationId, loading } = action.payload;
      if (!locationId) return;
      entryFor(state, locationId).loading = loading;
    },
    setPaymentsToCheck: (
      state,
      action: PayloadAction<{
        locationId: string;
        items: PaymentToCheck[];
        loadedAt: string;
      }>,
    ) => {
      const { locationId, items, loadedAt } = action.payload;
      if (!locationId) return;
      const entry = entryFor(state, locationId);
      entry.items = items;
      entry.loadedAt = loadedAt;
      entry.loading = false;
      entry.error = false;
    },
    setPaymentsToCheckError: (
      state,
      action: PayloadAction<{ locationId: string }>,
    ) => {
      const { locationId } = action.payload;
      if (!locationId) return;
      const entry = entryFor(state, locationId);
      entry.loading = false;
      entry.error = true;
    },
    setPaymentToCheckBusy: (
      state,
      action: PayloadAction<{ locationId: string; busyId: string | null }>,
    ) => {
      const { locationId, busyId } = action.payload;
      if (!locationId) return;
      entryFor(state, locationId).busyId = busyId;
    },
    /** The server's view after an action: replaced in place, or removed once settled. */
    upsertPaymentToCheckItem: (
      state,
      action: PayloadAction<{ locationId: string; item: PaymentToCheck }>,
    ) => {
      const { locationId, item } = action.payload;
      if (!locationId) return;
      const entry = entryFor(state, locationId);
      entry.items = upsertPaymentToCheck(entry.items, item);
    },
    clearPaymentsToCheckLocation: (state, action: PayloadAction<string>) => {
      delete state.byLocation[action.payload];
    },
  },
});

export const {
  setPaymentsToCheckLoading,
  setPaymentsToCheck,
  setPaymentsToCheckError,
  setPaymentToCheckBusy,
  upsertPaymentToCheckItem,
  clearPaymentsToCheckLocation,
} = mpesaPaymentsToCheckSlice.actions;

export default mpesaPaymentsToCheckSlice.reducer;

// Selectors
import type { RootState } from '../types';

const EMPTY_ENTRY: MpesaPaymentsToCheckLocationEntry = Object.freeze(
  emptyEntry(),
);

export const selectPaymentsToCheckEntry = (
  state: RootState,
  locationId: string,
): MpesaPaymentsToCheckLocationEntry =>
  (locationId &&
    state.backofficeMpesaPaymentsToCheck.byLocation[locationId]) ||
  EMPTY_ENTRY;

export const selectPaymentToCheck = (
  state: RootState,
  locationId: string,
  stkRequestId: string,
): PaymentToCheck | undefined =>
  selectPaymentsToCheckEntry(state, locationId).items.find(
    item => String(item.bp_stk_request_id) === String(stkRequestId),
  );
